// Third party components
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { Divider, Grid, Typography, Card, CardContent, Container, Skeleton } from '@mui/material';
import ThemeMap from 'components/ThemeMap';

// Shared components
import { LineLoader } from '@timmons-group/shared-react-components';
import { useMapConfig } from '@timmons-group/shared-react-components/hooks';
import { transformExtent } from 'ol/proj';

// own components
import ProfileDetails from '../../components/ProfileDetails';

const UnitMap = ({ data, config, basemap }) => {
  const olMap = useRef(null);
  const oThemeMap = useRef(null);
  const themeMapInstance = useRef(null);
  const [features, setFeatures] = useState([]);
  const [auxFeatures, setAuxFeatures] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [extent, setExtent] = useState(null);

  const setMap = (olm) => {
    oThemeMap.current = olm;
    // olMap is a ref to the map. So we need to get the .current property
    olMap.current = olm.olMap.current;
    setMapReady(true);
  };

  useEffect(() => {
    if (mapReady) {
      if (data && data.geometry) {
        setFeatures([data.geometry]);
        setAuxFeatures([
          ...data?.practices?.practiceLines?.map(g => g.geometry) || [],
          ...data?.practices?.practicePoints?.map(g => g.geometry) || [],
          ...data?.practices?.practicePolygons?.map(g => g.geometry) || [],
        ]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  useEffect(() => {
    if (mapReady && features.length > 0 && themeMapInstance.current) {
      const updatedExt = transformExtent(themeMapInstance.current?.getFeatureLayerExtent(), 'EPSG:3857', 'EPSG:4326');
      setExtent(updatedExt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features]);

  useEffect(() => {
    toggleBasemap(basemap);
  }, [basemap]);

  // Basemap toggle
  const toggleBasemap = (basemapKey) => {
    // If the map isn't ready, do nothing
    if (!olMap.current) return;

    // Get the current basemap key - these keys are set in the DB config
    const basemaps = olMap.current.themes.getCategoryByKey('cat_unit_detail_basemaps');
    const allowableBasemapKeys = basemaps.config.layers.map((l) => l.key);
    const selectedBasemapKeys = basemaps.getSelectedLayers().map((l) => l.key);

    // If the selectedBasemapKeys contains the basemapKey we're trying to select, do nothing
    if (selectedBasemapKeys.includes(basemapKey)) return;

    // If the selected basemap is not in the list of allowable basemaps, do nothing
    if (!allowableBasemapKeys.includes(basemapKey)) return;

    // Select the new basemap
    basemaps.selectLayer(basemapKey);
  };

  if (!config) {
    return <p>No map config found</p>;
  }

  return (
    <ThemeMap
      ref={themeMapInstance}
      existingFeatureLayer="unit_details"
      existingAuxFeatureLayer="practice_details"
      defaultExtent={extent}
      themes={config}
      allowFeatures={true}
      features={features}
      allowAuxFeatures={true}
      auxFeatures={auxFeatures}
      mapSet={setMap}
    />
  );
}

UnitMap.propTypes = {
  data: PropTypes.object.isRequired,
  config: PropTypes.object,
}

const UnitDetails = ({ unit, loading }) => {
  const [mapConfig, mapConfigLoading] = useMapConfig('unit_detail');
  // Currently hard coded to match our DB config "lyr_esri_topo" - alternatively we could
  // populate this from the active map, seems unecessary at this point though
  const [currentBasemap, setCurrentBasemap] = useState('lyr_esri_topo');

  const renderDetails = () => {
    if (loading) {
      return <>
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </>
    }

    return (
      <>
        <ProfileDetails
          item={unit}
          profileItemType="unit"
        />
      </>
    );
  }

  // Basemap toggle
  const toggleBasemap = (_event, basemapKey) => {
    // Call toggle basemap on UnitMap
    setCurrentBasemap(basemapKey);
  };

  return (
    <Container maxWidth={false} sx={{ marginTop: 2 }}>

      <Card>

        {/* Header */}
        <CardContent>
          <Typography variant="sectionHeader">Unit Profile</Typography>
        </CardContent>

        <Divider />

        {/* Body */}
        <CardContent>
          {/* Basemap Toggle */}
          {!mapConfigLoading && <ToggleButtonGroup
            color="secondary"
            value={currentBasemap}
            exclusive
            onChange={toggleBasemap}
            aria-label="Basemap Toggle"
            sx={{
              position: 'relative', top: '4rem', left: '1rem',
              zIndex: 1000,
              background: '#fff',
              border: '1px solid black'
            }}
          >
            <ToggleButton className="ToggleButton" value="lyr_esri_topo">Topo</ToggleButton>
            <ToggleButton className="ToggleButton" value="lyr_esri_world_image">Imagery</ToggleButton>
          </ToggleButtonGroup>}

          {/* Main Card Container */}
          <Grid container spacing={0}>

            {/* Map Container */}
            <Grid item xs={12} md={6}>
              {mapConfigLoading || loading || !unit ?
                <LineLoader message="Loading Map..." color="secondary" />
                :
                <UnitMap data={unit} config={mapConfig} basemap={currentBasemap} />
              }
            </Grid>

            {/* Details Container */}
            <Grid item xs={12} md={6} sx={{ paddingLeft: 3, paddingRight: 3 }}>
              <Typography variant="sectionHeader">Details</Typography>
              <Divider />
              {renderDetails()}
            </Grid>

          </Grid>

        </CardContent>

      </Card>

    </Container>
  );
};

UnitDetails.propTypes = {
  unit: PropTypes.object,
  loading: PropTypes.bool,
}

export default UnitDetails;
